import { useState, useEffect } from "react";
import { TbDatabaseX } from "react-icons/tb";
import { MdEdit, MdDelete, MdInfo } from "react-icons/md";
import { BsCloudCheck, BsCloudSlash } from "react-icons/bs";
import MySQLEditModal from "./edit-modal-components/mysql-edit-modal";
import PostgreSQLEditModal from "./edit-modal-components/postgress-edit-modal";
import { useGlobalStore } from "~/stores/store-provider";
import { Connection } from "~/stores/slices/database";
import { Tooltip } from "react-tooltip";

const DatabaseConnectionList = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [connectionType, setConnectionType] = useState("");

  const {
    connectionsList,
    getConnectionsList,
    getConnectionDetails,
    deleteConnection,
  } = useGlobalStore((state) => state);

  useEffect(() => {
    getConnectionsList();
  }, []);

  const handleDeleteConnection = async (connectionId: string) => {
    await deleteConnection(connectionId);
  };

  const handleEditConnection = async (connection: Connection) => {
    await getConnectionDetails(connection);
    setConnectionType(connection.connection_type);
    setIsDialogOpen(true);
  };

  const getBadgeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case "postgresql":
        return "badge badge-outline badge-neutral text-blue-600 border-blue-600";
      case "mysql":
        return "badge badge-outline badge-neutral text-orange-600 border-orange-600";
      case "snowflake":
        return "badge badge-outline badge-neutral text-cyan-600 border-cyan-600";
      case "shopify":
        return "badge badge-outline badge-neutral text-green-600 border-green-600";
      case "metabase":
        return "badge badge-outline badge-neutral text-purple-600 border-purple-600";
      default:
        return "badge badge-outline badge-neutral text-neutral border-neutral";
    }
  };

  const renderShopifySyncStatus = (syncStatus?: string, connectionId?: string) => {
    if (!syncStatus) {
      return null;
    }

    const tooltipId = `shopify-sync-${connectionId}`;

    if (syncStatus === "COMPLETED") {
      return (
        <>
          <BsCloudCheck
            size={20}
            className="text-success my-auto"
            data-tooltip-content="Sync completed"
            data-tooltip-id={tooltipId}
          />
          <Tooltip id={tooltipId} />
        </>
      );
    } else if (syncStatus === "FAILED") {
      return (
        <>
          <BsCloudSlash
            size={20}
            className="text-error my-auto"
            data-tooltip-content="Sync failed"
            data-tooltip-id={tooltipId}
          />
          <Tooltip id={tooltipId} />
        </>
      );
    } else if (syncStatus === "IN_SYNC" || syncStatus === "PENDING") {
      return (
        <>
          <span
            className="loading loading-spinner text-secondary loading-xs"
            data-tooltip-id={tooltipId}
            data-tooltip-content="Data Sync in Progress"
          />
          <Tooltip id={tooltipId} />
        </>
      );
    }

    return null;
  };

  return (
    <>
      <div className="flex flex-col gap-3 mb-4">
        <h3 className="text-md font-semibold">Added Connections</h3>
        {connectionsList.length > 0 ? (
          <div className="overflow-x-auto bg-base-100 my-2 h-[270px] overflow-y-auto scrollbar-none ">
            <table className="table table-zebra table-pin-rows">
              <thead className="sticky">
                <tr>
                  <th>Connection</th>
                  <th>Type</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {connectionsList.map(
                  (connection: Connection, index: number) => (
                    <tr key={index}>
                      <td className="max-w-[100px] overflow-hidden text-ellipsis whitespace-nowrap">
                        <div className="flex items-center gap-2 text-sm">
                          {connection.description &&
                            connection.description.trim() !== "" && (
                              <>
                                <MdInfo
                                  size={14}
                                  className="text-info cursor-help"
                                  data-tooltip-id={`tooltip-${connection._id}`}
                                  data-tooltip-content={connection.description}
                                />
                                <Tooltip
                                  id={`tooltip-${connection._id}`}
                                  place="right"
                                  className="bg-base-300 text-base-content border border-base-content/20 text-xs rounded-lg p-2 shadow-lg max-w-sm break-words whitespace-normal backdrop-blur-sm opacity-100"
                                />
                              </>
                            )}
                          {connection.name}
                        </div>
                      </td>
                      <td className="max-w-[100px]">
                        {connection.name.includes("Metabase") && (
                          <span
                            className={`mr-2 ${getBadgeColor("metabase")}`}
                            role="status"
                            aria-label="Metabase connection"
                          >
                            metabase
                          </span>
                        )}
                        <span
                          className={`${getBadgeColor(
                            connection.connection_type
                          )}`}
                          role="status"
                          aria-label={`${connection.connection_type} connection`}
                        >
                          {connection.connection_type}
                        </span>
                      </td>
                      <td className="max-w-[50px] overflow-visible">
                        <div className="flex gap-2">
                          {connection.connection_type !== "shopify" && (
                            <button
                              className="btn btn-ghost btn-circle btn-sm text-blue-400 hover:text-blue-700 hover:bg-blue-200"
                              onClick={() => handleEditConnection(connection)}
                            >
                              <MdEdit size={16} />
                            </button>
                          )}
                          <button
                            className="btn btn-ghost btn-circle btn-sm text-red-500 hover:text-red-700 hover:bg-red-200 disabled:text-gray-400"
                            onClick={() =>
                              handleDeleteConnection(connection._id)
                            }
                          >
                            <MdDelete size={16} />
                          </button>
                          {connection.connection_type === "shopify" &&
                            renderShopifySyncStatus(connection.sync_status, connection._id)}
                        </div>
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="card bg-base-100 my-4 h-48">
            <div className="card-body flex flex-col items-center justify-center gap-2">
              <TbDatabaseX size={25} />
              <h1 className="text-xl font-semibold">No Connections</h1>
              <p className="text-sm text-gray-500">
                Link your database to begin exploring and analyzing your data
                insights.
              </p>
            </div>
          </div>
        )}
      </div>
      {connectionType.toLowerCase().includes("mysql") && (
        <MySQLEditModal
          open={isDialogOpen}
          onOpenChange={(open) => setIsDialogOpen(open)}
        />
      )}
      {connectionType.toLowerCase().includes("postgresql") && (
        <PostgreSQLEditModal
          open={isDialogOpen}
          onOpenChange={(open) => setIsDialogOpen(open)}
        />
      )}
    </>
  );
};

export default DatabaseConnectionList;
